/**
 * ⟦SPEC-PRODUCTEUR §5 · M3.29⟧ **Le manager — le troisième étage de l'escalier de délégation.**
 *
 * Le premier étage (le marché, `freelances.ts`) retire **l'adresse** ; le deuxième (le
 * producteur, `producteur.ts`) retire **le choix de quoi faire** ; celui-ci retire **le
 * rythme** — dormir, veiller, souffler, tenir la caisse.
 *
 * 🔑 **Ce que les 16 PC achètent, dit exactement (§5.4bis)** : pas une action de plus, pas une
 * journée de plus — **deux motifs d'arrêt en moins**. Sans manager, la délégation s'arrête sur
 * la santé mentale et sur la trésorerie ; avec lui, il les absorbe et continue :
 *
 *  - `sante_mentale` — selon la politique de `repos` : `jamais` → il interrompt encore ;
 *    `therapie` → il paie 150 ₵ et continue ; `tout` → il annonce la pause UNE fois et la pose ⟦A-2⟧ (le sommeil
 *    l'engage : voir `prochaineActionEquipe`, ⟦C62 ter⟧) ;
 *  - `tresorerie` — il **replie le choix de freelance sur le moins cher qui rentre** tant que
 *    la caisse couvre moins de `SEUIL_TENSION_J` jours de salaires, et n'interrompt que si
 *    même le moins cher ne passe pas. Le repli est un choix qui coûte de la qualité : il fait
 *    tourner la boutique en la faisant tourner moins bien.
 *
 * ⛔ **`evenement` reste inabsorbable, à cet étage comme aux autres** — décision de Mathieu,
 * mot pour mot : *« je ne pense pas qu'il faut qu'il y ait des trucs qui automatisent la
 * fenêtre d'événement parce que là, sinon, tu n'as plus rien à jouer. »* Ni `palier`, ni
 * `competence`, ni `jalon`, ni `peril` : le manager rend les clés devant tout ce qui demande
 * une décision.
 *
 * Comme le producteur : **aucun code de résolution, aucun tirage propre** — il choisit des
 * `Action` existantes qui repassent par `resolve`, le même chemin que le joueur. L'état
 * neutre tient par construction : interrupteur `meta.manager_actif` éteint (le défaut), rien
 * ici n'est jamais lu.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`, aucun `draw`.
 */
import { hasCapability } from "./effects.js";
import { SYSTEMES_TOUS_ACTIFS, systemes } from "./menage.js";
import { draw, pickWeighted } from "./rng.js";
// ─────────────────────────────────────────────────────────────────────────────
// §3.3 Les consignes de rythme, et leurs défauts
// ─────────────────────────────────────────────────────────────────────────────
/** ⟦§13.1 ✎ 12⟧ Les défauts du rythme — c'est l'état neutre du cadran. */
export const RYTHME_DEFAUT = {
    cadence: "raisonnable",
    repos: "jamais",
    noeud_vise: null,
};
/**
 * Les consignes de rythme de la run. **Elles ne sont lues que si le manager travaille** : sans
 * lui, la boucle tourne aux défauts (`raisonnable` / `jamais` / aucun nœud visé) — le monde
 * d'avant M3.29 au caractère près, même si un état sérialisé porte un réglage.
 */
export function consignesRythme(consignes, managerTravaille, 
/**
 * ⟦C62 bis · A-179⟧ Le repos tant que le joueur n'a RIEN réglé (`reposParDefaut(content)`, `menage.ts`). `"jamais"` (le défaut
 * de l'appel) = le jeu d'avant. Une consigne écrite par le joueur gagne toujours, même « jamais ».
 */
reposParDefaut = RYTHME_DEFAUT.repos) {
    if (!managerTravaille)
        return RYTHME_DEFAUT;
    return consignes?.rythme ?? rythmeParDefaut(reposParDefaut);
}
/**
 * ⟦C62 bis · A-179⟧ Le rythme d'une run dont le joueur n'a rien réglé : les défauts d'avant, sauf le repos, que le contenu
 * peut automatiser (`meta.repos_par_defaut`). Sans repos demandé, c'est `RYTHME_DEFAUT` lui-même (même objet).
 */
export function rythmeParDefaut(repos = RYTHME_DEFAUT.repos) {
    return repos === RYTHME_DEFAUT.repos ? RYTHME_DEFAUT : { ...RYTHME_DEFAUT, repos };
}
/**
 * ⟦§5.3 ✎ 10⟧ **La cadence est le seul cadran qui porte un risque, et c'est voulu** :
 * `soutenue` publie tant que l'énergie couvre le coût — donc jusque dans la zone de crunch
 * (Qualité ×0,7, SM −5) et la zone rouge, où les événements négatifs pèsent double.
 */
export const CADENCES = {
    soutenue: { marge_energie: 0, seuil_sm: 30, crunch: true },
    raisonnable: { marge_energie: 20, seuil_sm: 45, crunch: false },
    econome: { marge_energie: 40, seuil_sm: 60, crunch: false },
};
// ─────────────────────────────────────────────────────────────────────────────
// L'interrupteur, la capacité, l'activité
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦A-77 §4⟧ L'étage 3 a son PROPRE interrupteur, en ET avec celui de l'escalier : un manager
 * sans producteur tiendrait le calendrier d'une chaîne qui ne publie pas.
 */
export function managerActive(content) {
    return content.meta?.manager_actif === true && content.meta?.equipe_active === true;
}
/** Le joueur possède-t-il le nœud *Le manager* ? */
export function managerPossede(mods) {
    return hasCapability(mods, "rythme_delegue");
}
/**
 * ⟦§1.2 / §6.3⟧ Le manager travaille-t-il ? Interrupteur allumé, nœud possédé, pas congédié —
 * **et le producteur actif** : congédier le producteur l'éteint de fait (il n'a plus rien à
 * faire produire), c'est la règle du §6.3 et T-PROD-10 la verrouille.
 */
export function managerActif(state, content, mods, producteurEstActif) {
    if (!managerActive(content) || !managerPossede(mods))
        return false;
    if (state.flags.manager_actif === false)
        return false;
    return producteurEstActif;
}
// ─────────────────────────────────────────────────────────────────────────────
// §6.2 Le salaire — le DEUXIÈME escalier de paie, indépendant du premier
// ─────────────────────────────────────────────────────────────────────────────
/** ⟦§13.1 ✎ 3-4⟧ Les défauts, quand `content/producteur.json` serait absent. */
export const SALAIRE_MANAGER_J_DEFAUT = 25;
export const PRIME_RETOUR_MANAGER_DEFAUT = 250;
/**
 * ⟦C8 · A-120⟧ Le candidat recruté (`state.flags.manager_candidat_id` → `content.managers`),
 * ou le défaut compat : **neutre, expérimenté**, dérivé de `producteur.manager` (Gérard
 * Planning — « vingt ans de plateaux, zéro sentiment »). C'est le seul manager qui ait jamais
 * existé avant ce chantier, et il n'avait ni alignement ni expérience déclarés : neutre +
 * forte reproduit exactement son comportement d'avant l'incarnation (le meilleur bilan brut,
 * choisi sans se tromper) — un état sauvegardé sans recrutement explicite ne change rien.
 *
 * `candidatIdOverride`, s'il est fourni, l'emporte sur `state.flags.manager_candidat_id` :
 * sert à valider/facturer une recrue **avant** que `regler_equipe` n'écrive le drapeau
 * (⟦A-11 §1⟧ « le coût validé est le coût modifié »).
 */
export function managerChoisi(state, content, candidatIdOverride) {
    const id = candidatIdOverride ?? state.flags.manager_candidat_id;
    const candidat = id ? content.managers?.find((m) => m.id === id) : undefined;
    if (candidat) {
        return {
            id: candidat.id,
            alignement: candidat.alignement,
            experience: candidat.experience,
            salaire_jour: candidat.salaire_jour,
            prime_retour: candidat.prime_retour,
        };
    }
    return {
        alignement: "neutre",
        experience: "forte",
        salaire_jour: content.producteur?.manager?.salaire_jour ?? SALAIRE_MANAGER_J_DEFAUT,
        prime_retour: content.producteur?.manager?.prime_retour ?? PRIME_RETOUR_MANAGER_DEFAUT,
    };
}
export function salaireManager(state, content, candidatIdOverride) {
    return managerChoisi(state, content, candidatIdOverride).salaire_jour;
}
export function primeRetourManager(state, content, candidatIdOverride) {
    return managerChoisi(state, content, candidatIdOverride).prime_retour;
}
/**
 * ⟦§6.2⟧ Le prélèvement du salaire du manager, à chaque frontière de jour, **après** celui du
 * producteur — même escalier de défaut, compteur SÉPARÉ (`manager_jours_impayes`) : les deux
 * paies sont indépendantes, et elles ne tombent pas au même moment (T-PROD-10).
 */
export function paierManager(state, content, mods, producteurEstActif) {
    if (!managerActif(state, content, mods, producteurEstActif))
        return null;
    const salaire = salaireManager(state, content);
    if (state.money >= salaire) {
        state.money -= salaire;
        state.counters.manager_jours_impayes = 0;
        return { paye: salaire, impaye: false, parti: false };
    }
    const impayes = (state.counters.manager_jours_impayes ?? 0) + 1;
    state.counters.manager_jours_impayes = impayes;
    const grace = content.producteur?.jours_grace ?? 3;
    const parti = impayes >= grace;
    if (parti)
        state.flags.manager_actif = false;
    return { paye: 0, impaye: true, parti };
}
// ─────────────────────────────────────────────────────────────────────────────
// §5.4bis La tension de trésorerie — quand le repli sur le moins cher s'engage
// ─────────────────────────────────────────────────────────────────────────────
/** ⟦§13.1 ✎ 7⟧ Le seuil du repli : moins de 14 jours de lignes fixes en caisse. */
export const SEUIL_TENSION_J = 14;
/**
 * ⟦A-92⟧ Les charges fixes d'un jour simulé — **la règle de `chargesDuJour` (`actions.ts`,
 * pas 5b de SPEC-MOTEUR §2.3), dupliquée avec sa source nommée plutôt qu'importée**, parce
 * qu'`actions.ts` importe `producteur.ts` qui importe ce module (même précédent que
 * `themeEpuise` dans `choisirProduction`). `producteur.test.ts` vérifie que les deux lectures
 * restent égales dans les trois états : avant le Grand Saut ⟦A-1⟧, au studio, chez les
 * parents ⟦A-11 §2⟧.
 */
export function chargesFixesDuJour(state, sys = SYSTEMES_TOUS_ACTIFS) {
    // ⟦C60 · A-177⟧ Même coupure que `chargesDuJour` : plus de loyer, donc plus de charges dans la
    // tension de trésorerie, le coussin du manager ne compte plus que les salaires.
    if (!sys.charges)
        return 0;
    if (!state.flags.grand_saut_done)
        return 0;
    if (state.flags.chez_les_parents === true)
        return 0;
    return 5; // `CHARGES_FIXES_PAR_JOUR` (actions.ts)
}
/**
 * ⟦§6.2 · A-92⟧ **Les lignes fixes d'un jour** : les charges, le salaire du producteur, et
 * celui du manager quand il travaille. C'est l'unité de compte de la « tension » (§5.4bis) et
 * du coussin du producteur (§4.5) — la même pour les deux étages, pour qu'aucune bande de
 * caisse ne tombe entre les deux règles.
 */
export function lignesFixesDuJour(state, content, managerTravaille) {
    return (chargesFixesDuJour(state, systemes(content)) +
        salaireProducteurDepuisContenu(state, content) +
        (managerTravaille ? salaireManager(state, content) : 0));
}
/**
 * ⟦C8 · A-120⟧ Le salaire du producteur CHOISI, lu depuis CE contenu — même duplication que
 * `chargesFixesDuJour` ci-dessus et pour la même raison (`producteur.ts` importe ce module) :
 * `producteur.test.ts` vérifie que les deux lectures (celle-ci et `producteur.ts::
 * salaireProducteur`) restent égales.
 */
function salaireProducteurDepuisContenu(state, content) {
    const id = state.flags.producteur_candidat_id;
    const candidat = id ? content.producteur?.candidats_producteur?.find((c) => c.id === id) : undefined;
    return candidat?.salaire_jour ?? content.producteur?.producteur.salaire_jour ?? 15;
}
/** ⟦§5.4bis⟧ Le seuil de tension : `SEUIL_TENSION_J` jours de lignes fixes. */
export function seuilTension(state, content, managerTravaille) {
    // ⟦C60 bis · A-177⟧ Le nombre de jours est un réglage du contenu (`systemes.coussin_jours`), 14 par défaut
    // (`SEUIL_TENSION_J`) : même coussin pour le producteur (`coussinProducteur`) et même seuil de tension pour le manager.
    return systemes(content).coussin_jours * lignesFixesDuJour(state, content, managerTravaille);
}
/**
 * ⟦§5.4bis⟧ La caisse couvre-t-elle moins de `SEUIL_TENSION_J` jours de lignes fixes ? C'est
 * le déclencheur du repli sur le moins cher — une réponse d'intendance, pas de production.
 * N'est lue que si le manager travaille : ses lignes comptent.
 */
export function tensionTresorerie(state, content) {
    return state.money < seuilTension(state, content, true);
}
/**
 * ⟦C8 §2 ✎⟧ Poids de conversion entre monnaies pour rendre `argent` (₵, non borné) et
 * `subs_flat` (abonnés bruts) comparables aux jauges 0-100 et à `subs_pct` (±15) : sans eux,
 * un `gauge_delta argent` de 300 écraserait tout le reste du score. Valeurs ✎, à ajuster
 * quand du contenu réel exercera ces branches — aucun événement v1 ne porte encore d'effet
 * `argent` sur un choix.
 */
const POIDS_ARGENT = 1 / 20;
const POIDS_SUBS_FLAT = 1 / 100;
/** La croissance brute d'un choix : abonnés + argent — ce que le style CHAOS recherche. */
function scoreCroissance(effets) {
    let score = 0;
    for (const e of effets) {
        if (e.type === "subs_pct")
            score += e.value;
        else if (e.type === "subs_flat")
            score += e.value * POIDS_SUBS_FLAT;
        else if (e.type === "gauge_delta" && e.gauge === "argent")
            score += e.value * POIDS_ARGENT;
    }
    return score;
}
/** Réputation + fidélité d'un choix — ce que le style LUMIÈRE recherche. */
function scoreReputationFidelite(effets) {
    let score = 0;
    for (const e of effets) {
        if (e.type === "gauge_delta" && (e.gauge === "reputation" || e.gauge === "fidelite")) {
            score += e.value;
        }
    }
    return score;
}
/**
 * Score d'un choix pour UN style — dérivé des `effets` existants, jamais d'un tagging de
 * contenu neuf (C8 §2, contrainte explicite du brief) :
 *  - **lumière** : réputation/fidélité, MOINS la casse infligée à la croissance (« moins de
 *    risque ») ;
 *  - **chaos** : la croissance brute, prise telle quelle — l'« audace » lue comme « aucune
 *    pénalité de risque » ;
 *  - **neutre** : « le meilleur bilan brut » — croissance + réputation/fidélité, sans biais.
 */
function scoreStyle(effets, alignement) {
    const croissance = scoreCroissance(effets);
    const repFid = scoreReputationFidelite(effets);
    switch (alignement) {
        case "lumiere":
            return repFid - Math.max(0, -croissance);
        case "chaos":
            return croissance;
        case "neutre":
            return croissance + repFid;
    }
}
/** Plancher > 0 ajouté à chaque poids de tirage (expérience faible) — aucun choix à poids nul. */
const POIDS_FAIBLE_PLANCHER = 0.5;
/**
 * ⟦C8 §2⟧ **Le manager tranche** un événement, PUR : aucune I/O, aucun `Math.random` — tout
 * l'aléa passe par `draw` (purpose fermé `manager_choice`, RNG §3.2). Rend l'id du choix
 * retenu parmi `disponibles` (déjà filtrés par `choixDisponible`, `events.ts` — ce module ne
 * connaît pas cette garde, voir `ChoixNotable`).
 *
 * - **Expérience forte** : LA meilleure issue de son style (égalité ⇒ la première de la
 *   liste, l'ordre du contenu — déterministe, jamais un second tirage).
 * - **Expérience faible** : un tirage parmi les disponibles, biaisé vers le style (poids =
 *   score décalé au-dessus de zéro, jamais un poids nul).
 *
 * Écart signalé face à la lettre du brief (`managerTranche(state, event, manager)`) : cette
 * fonction reçoit la liste déjà filtrée plutôt que l'événement entier, pour rester une
 * FEUILLE — `choixDisponible` vit dans `events.ts` (elle lit `flagPose`, un dérivé du moteur
 * bien plus large qu'une garde de manager), et la dupliquer ici aurait ouvert exactement le
 * risque de dérive qu'⟦A-119⟧ a débusqué sur `flagPose` lui-même.
 */
export function managerTranche(state, disponibles, manager, seed) {
    if (disponibles.length === 0) {
        throw new Error("managerTranche: aucun choix disponible à trancher");
    }
    if (disponibles.length === 1)
        return disponibles[0].id;
    const notes = disponibles.map((c) => scoreStyle(c.effets, manager.alignement));
    if (manager.experience === "forte") {
        let meilleur = 0;
        for (let i = 1; i < notes.length; i++) {
            if (notes[i] > notes[meilleur])
                meilleur = i;
        }
        return disponibles[meilleur].id;
    }
    // Expérience faible : tirage biaisé — un seul `draw`, purpose `manager_choice` (§3.2).
    const plancher = Math.min(...notes);
    const poids = notes.map((n) => n - plancher + POIDS_FAIBLE_PLANCHER);
    const u = draw(seed, state.turn, "manager_choice", 0);
    const i = pickWeighted(poids, u);
    return disponibles[i === -1 ? 0 : i].id;
}
//# sourceMappingURL=manager.js.map